import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  applyCapture,
  applyEntryTaskSuggestions,
  detectEntryTasks,
  discardCapture,
  getCaptureSummary,
  getVoiceSettings,
  giveVoiceConsent,
  listCaptures,
  listEntryTaskSuggestions,
  reanalyseCapture,
  retryCapture,
  undoCapture,
  updateCapture,
  withdrawVoiceConsent,
  type UpdateCaptureInput,
} from '../services/captureService';
import { invalidateAllTasks } from './useTasks';
import type { ApplyRequest, CaptureDto } from '../types/capture';

/** Query keys of the voice journal ("To review" inbox, summary counts, settings). */
export const capturesQueryKey = ['captures'] as const;
export const captureKeys = {
  all: capturesQueryKey,
  list: () => [...capturesQueryKey, 'list'] as const,
  summary: () => [...capturesQueryKey, 'summary'] as const,
  settings: () => [...capturesQueryKey, 'settings'] as const,
  entryTasks: (aquariumId: string, entryId: string) => [...capturesQueryKey, 'entry-tasks', aquariumId, entryId] as const,
};

/** Statuses that are still on their way to "review" — poll quickly while one exists. */
export const PROCESSING_STATUSES: ReadonlySet<CaptureDto['status']> = new Set(['queued', 'transcribing', 'analyzing']);

const FAST_POLL_MS = 3_000;
const SLOW_POLL_MS = 60_000;

/** Feature switch, consent and pricing of voice notes (GET /api/v1/me/voice). */
export function useVoiceSettings(enabled = true) {
  return useQuery({
    queryKey: captureKeys.settings(),
    queryFn: getVoiceSettings,
    enabled,
    staleTime: 60_000,
  });
}

export function useVoiceConsentMutations() {
  const queryClient = useQueryClient();
  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: captureKeys.settings() });
  };
  const give = useMutation({ mutationFn: (version: string) => giveVoiceConsent(version), onSuccess: refresh });
  const withdraw = useMutation({ mutationFn: () => withdrawVoiceConsent(), onSuccess: refresh });
  return { give, withdraw };
}

/**
 * Counts for the Capture tab, the "To review" sidebar item and the Note ready
 * banner. Polls every 3 s while a capture is being processed, else every minute.
 */
export function useCaptureSummary(enabled = true) {
  return useQuery({
    queryKey: captureKeys.summary(),
    queryFn: getCaptureSummary,
    enabled,
    staleTime: 2_000,
    refetchInterval: (query) => ((query.state.data?.processing ?? 0) > 0 ? FAST_POLL_MS : SLOW_POLL_MS),
    refetchIntervalInBackground: false,
  });
}

/** Every open capture plus the ones saved in the last 24 h ("Recently saved"). */
export function useCaptureList(enabled = true) {
  return useQuery({
    queryKey: captureKeys.list(),
    queryFn: listCaptures,
    enabled,
    staleTime: 2_000,
    refetchInterval: (query) =>
      (query.state.data ?? []).some((c) => PROCESSING_STATUSES.has(c.status)) ? FAST_POLL_MS : false,
  });
}

/** Replaces one capture in the cached list (after a mutation answered with it). */
function putCapture(queryClient: ReturnType<typeof useQueryClient>, capture: CaptureDto): void {
  queryClient.setQueryData<CaptureDto[]>(captureKeys.list(), (old) =>
    old ? old.map((c) => (c.id === capture.id ? capture : c)) : old,
  );
}

export function useCaptureMutations() {
  const queryClient = useQueryClient();
  const refreshAll = (): void => {
    void queryClient.invalidateQueries({ queryKey: capturesQueryKey });
  };
  const onCapture = (capture: CaptureDto): void => {
    putCapture(queryClient, capture);
    refreshAll();
  };

  const update = useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateCaptureInput }) => updateCapture(id, input),
    onSuccess: onCapture,
  });
  const reanalyse = useMutation({
    mutationFn: ({ id, transcript, language }: { id: string; transcript?: string; language?: string }) =>
      reanalyseCapture(id, { transcript, language }),
    onSuccess: onCapture,
  });
  const apply = useMutation({
    mutationFn: ({ id, request }: { id: string; request: ApplyRequest }) => applyCapture(id, request),
    onSuccess: (result) => {
      onCapture(result.capture);
      // Saved items may be tasks, journal entries, measurements or dosing logs.
      invalidateAllTasks(queryClient);
      void queryClient.invalidateQueries({ queryKey: ['journalEntries'] });
      void queryClient.invalidateQueries({ queryKey: ['measurements'] });
    },
  });
  const undo = useMutation({
    mutationFn: (id: string) => undoCapture(id),
    onSuccess: (capture) => {
      onCapture(capture);
      invalidateAllTasks(queryClient);
      void queryClient.invalidateQueries({ queryKey: ['journalEntries'] });
      void queryClient.invalidateQueries({ queryKey: ['measurements'] });
    },
  });
  const retry = useMutation({ mutationFn: (id: string) => retryCapture(id), onSuccess: onCapture });
  const discard = useMutation({
    mutationFn: (id: string) => discardCapture(id),
    onSuccess: (_void, id) => {
      queryClient.setQueryData<CaptureDto[]>(captureKeys.list(), (old) => (old ? old.filter((c) => c.id !== id) : old));
      refreshAll();
    },
  });

  return { update, reanalyse, apply, undo, retry, discard };
}

// ─── Typed journal entries (UC-B) ─────────────────────────────────────────────

/** Open task suggestions of a journal entry (from an earlier detection). */
export function useEntryTaskSuggestions(aquariumId: string, entryId: string, enabled = true) {
  return useQuery({
    queryKey: captureKeys.entryTasks(aquariumId, entryId),
    queryFn: () => listEntryTaskSuggestions(aquariumId, entryId),
    enabled: enabled && Boolean(aquariumId) && Boolean(entryId),
    staleTime: 30_000,
  });
}

export function useEntryTaskMutations(aquariumId: string, entryId: string) {
  const queryClient = useQueryClient();
  const key = captureKeys.entryTasks(aquariumId, entryId);

  const detect = useMutation({
    mutationFn: () => detectEntryTasks(aquariumId, entryId),
    onSuccess: (suggestions) => {
      queryClient.setQueryData(key, suggestions);
    },
  });
  const apply = useMutation({
    mutationFn: (request: Pick<ApplyRequest, 'items' | 'dismiss'>) => applyEntryTaskSuggestions(aquariumId, entryId, request),
    onSuccess: (result) => {
      queryClient.setQueryData(key, result.suggestions);
      invalidateAllTasks(queryClient);
      void queryClient.invalidateQueries({ queryKey: ['journalEntries'] });
    },
  });
  return { detect, apply };
}
